import { useState, useEffect } from 'react';
import { Calendar, Plus, Search, Edit2, Trash2, Save, X, ChevronDown } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

interface DolasciOdlasciEntry {
  id: string;
  name: string;
  email: string | null;
  month: string;
  recruiter: string;
  status: string | null;
  svoj_trosak: string | null;
  subitems: string | null;
  yard_recovery: string | null;
  cdl_scan: string | null;
  length: string | null;
  type: 'dolasci' | 'odlasci';
  created_at: string;
  updated_at: string;
}

export function DolasciOdlasciView() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [entries, setEntries] = useState<DolasciOdlasciEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterRecruiter, setFilterRecruiter] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<DolasciOdlasciEntry>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [dolasciExpanded, setDolasciExpanded] = useState(true);
  const [odlasciExpanded, setOdlasciExpanded] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [recruiters, setRecruiters] = useState<string[]>([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedEntryForStatus, setSelectedEntryForStatus] = useState<DolasciOdlasciEntry | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [newEntry, setNewEntry] = useState<Partial<DolasciOdlasciEntry>>({
    name: '',
    email: '',
    month: '',
    recruiter: '',
    status: '',
    svoj_trosak: '',
    subitems: '',
    yard_recovery: '',
    cdl_scan: '',
    length: '',
    type: 'dolasci'
  });

  const months = [
    'January 2026', 'February 2026', 'March 2026', 'April 2026', 'May 2026', 'June 2026',
    'July 2026', 'August 2026', 'September 2026', 'October 2026', 'November 2026', 'December 2026'
  ];

  const statusOptions = ['company', 'lease/rent', 'owner', 'stari vozac'];
  const svojTrosakOptions = ['Yes', 'No', 'Owner', 'stari vozac'];

  useEffect(() => {
    fetchEntries();
    fetchRecruiters();
  }, []);

  const fetchRecruiters = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('role', 'Recruiting');

      if (error) throw error;

      const recruiterNames = data
        ?.map((profile: any) => profile.full_name)
        .filter((name: string | null) => name != null) || [];

      setRecruiters(recruiterNames);
    } catch (error) {
      console.error('Error fetching recruiters:', error);
      // Fallback to empty list if fetch fails
      setRecruiters([]);
    }
  };

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('dolasci_odlasci')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEntries(data || []);
    } catch (error) {
      console.error('Error fetching entries:', error);
      showToast('error', 'Failed to load entries');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newEntry.name || !newEntry.month || !newEntry.recruiter) {
      showToast('error', 'Please fill in required fields (Name, Month, Recruiter)');
      return;
    }

    try {
      const { data: userData } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user?.id)
        .single();

      const { error } = await supabase
        .from('dolasci_odlasci')
        .insert([{
          ...newEntry,
          company_id: userData?.company_id,
          created_by: user?.id
        }]);

      if (error) throw error;

      showToast('success', 'Entry added successfully');
      setShowAddForm(false);
      setNewEntry({
        name: '',
        email: '',
        month: '',
        recruiter: '',
        status: '',
        svoj_trosak: '',
        subitems: '',
        yard_recovery: '',
        cdl_scan: '',
        length: '',
        type: 'dolasci'
      });
      fetchEntries();
    } catch (error) {
      console.error('Error adding entry:', error);
      showToast('error', 'Failed to add entry');
    }
  };

  const handleEdit = (entry: DolasciOdlasciEntry) => {
    setEditingId(entry.id);
    setEditForm(entry);
  };

  const handleSave = async () => {
    if (!editingId) return;

    try {
      const { error } = await supabase
        .from('dolasci_odlasci')
        .update(editForm)
        .eq('id', editingId);

      if (error) throw error;

      showToast('success', 'Entry updated successfully');
      setEditingId(null);
      setEditForm({});
      fetchEntries();
    } catch (error) {
      console.error('Error updating entry:', error);
      showToast('error', 'Failed to update entry');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this entry?')) return;

    try {
      const { error } = await supabase
        .from('dolasci_odlasci')
        .delete()
        .eq('id', id);

      if (error) throw error;

      showToast('success', 'Entry deleted successfully');
      fetchEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      showToast('error', 'Failed to delete entry');
    }
  };

  const handleRowClick = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    );
  };

  const handleStatusClick = (entry: DolasciOdlasciEntry, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedEntryForStatus(entry);
    setNewStatus(entry.status || '');
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async () => {
    if (!selectedEntryForStatus) return;

    try {
      const { error } = await supabase
        .from('dolasci_odlasci')
        .update({ status: newStatus })
        .eq('id', selectedEntryForStatus.id);

      if (error) throw error;

      showToast('success', 'Status updated successfully');
      setShowStatusModal(false);
      setSelectedEntryForStatus(null);
      fetchEntries();
    } catch (error) {
      console.error('Error updating status:', error);
      showToast('error', 'Failed to update status');
    }
  };

  const handleEditSelected = () => {
    if (selectedIds.length === 1) {
      const entry = entries.find(e => e.id === selectedIds[0]);
      if (entry) {
        handleEdit(entry);
        setSelectedIds([]);
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length > 0) {
      if (window.confirm(`Are you sure you want to delete ${selectedIds.length} driver(s)?`)) {
        for (const id of selectedIds) {
          await handleDelete(id);
        }
        setSelectedIds([]);
      }
    }
  };

  const filteredEntries = entries.filter(entry => {
    const matchesSearch = entry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.recruiter.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMonth = !filterMonth || entry.month === filterMonth;
    const matchesRecruiter = !filterRecruiter || entry.recruiter === filterRecruiter;
    return matchesSearch && matchesMonth && matchesRecruiter;
  });

  const dolasciEntries = filteredEntries.filter(e => e.type === 'dolasci');
  const odlasciEntries = filteredEntries.filter(e => e.type === 'odlasci');

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'company': return 'bg-blue-100 text-blue-800';
      case 'lease/rent': return 'bg-green-100 text-green-800';
      case 'owner': return 'bg-purple-100 text-purple-800';
      case 'stari vozac': return 'bg-gray-100 text-gray-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getSvojTrosakColor = (svojTrosak: string | null) => {
    switch (svojTrosak) {
      case 'Yes': return 'bg-green-100 text-green-800';
      case 'No': return 'bg-red-100 text-red-800';
      case 'Owner': return 'bg-purple-100 text-purple-800';
      case 'stari vozac': return 'bg-gray-100 text-gray-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const renderTableRows = (entries: DolasciOdlasciEntry[]) => {
    if (entries.length === 0) {
      return (
        <tr>
          <td colSpan={10} className="px-4 py-12 text-center text-slate-500">
            No entries found.
          </td>
        </tr>
      );
    }

    return entries.map(entry => (
      <tr 
        key={entry.id} 
        onClick={() => editingId !== entry.id && handleRowClick(entry.id)}
        className={`hover:bg-slate-50 cursor-pointer transition-colors ${selectedIds.includes(entry.id) ? 'bg-blue-50' : ''}`}
      >
        {editingId === entry.id ? (
          <>
            <td className="px-4 py-3">
              <input
                type="checkbox"
                checked={selectedIds.includes(entry.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  setSelectedIds(prev => 
                    prev.includes(entry.id) 
                      ? prev.filter(id => id !== entry.id)
                      : [...prev, entry.id]
                  );
                }}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
            </td>
            <td className="px-4 py-3">
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
              />
            </td>
            <td className="px-4 py-3">
              <select
                value={editForm.email || ''}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
              >
                <option value="">None</option>
                <option value="RECRUTIING zvao">RECRUTIING zvao</option>
              </select>
            </td>
            <td className="px-4 py-3">
              <select
                value={editForm.month}
                onChange={(e) => setEditForm({ ...editForm, month: e.target.value })}
                className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
              >
                {months.map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
            </td>
            <td className="px-4 py-3">
              <select
                value={editForm.recruiter}
                onChange={(e) => setEditForm({ ...editForm, recruiter: e.target.value })}
                className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
              >
                {recruiters.map(recruiter => (
                  <option key={recruiter} value={recruiter}>{recruiter}</option>
                ))}
              </select>
            </td>
            <td className="px-4 py-3">
              <select
                value={editForm.status || ''}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
              >
                <option value="">Select</option>
                {statusOptions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </td>
            <td className="px-4 py-3">
              <select
                value={editForm.svoj_trosak || ''}
                onChange={(e) => setEditForm({ ...editForm, svoj_trosak: e.target.value })}
                className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
              >
                <option value="">Select</option>
                {svojTrosakOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </td>
            <td className="px-4 py-3">
              <select
                value={editForm.yard_recovery || ''}
                onChange={(e) => setEditForm({ ...editForm, yard_recovery: e.target.value })}
                className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
              >
                <option value="">None</option>
                <option value="Yard">Yard</option>
                <option value="Recovery">Recovery</option>
              </select>
            </td>
            <td className="px-4 py-3">
              <select
                value={editForm.cdl_scan || ''}
                onChange={(e) => setEditForm({ ...editForm, cdl_scan: e.target.value })}
                className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
              >
                <option value="">None</option>
                <option value="Not Needed">Not Needed</option>
                <option value="Posted">Posted</option>
              </select>
            </td>
            <td className="px-4 py-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editForm.length || ''}
                  onChange={(e) => setEditForm({ ...editForm, length: e.target.value })}
                  className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                />
                <button onClick={handleSave} className="p-1 text-green-600 hover:bg-green-50 rounded flex-shrink-0">
                  <Save className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    setEditingId(null);
                    setEditForm({});
                  }}
                  className="p-1 text-slate-600 hover:bg-slate-50 rounded flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </td>
          </>
        ) : (
          <>
            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={selectedIds.includes(entry.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  setSelectedIds(prev => 
                    prev.includes(entry.id) 
                      ? prev.filter(id => id !== entry.id)
                      : [...prev, entry.id]
                  );
                }}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
            </td>
            <td className="px-4 py-3 text-sm font-medium text-slate-900">{entry.name}</td>
            <td className="px-4 py-3 text-sm text-slate-600">{entry.email || '-'}</td>
            <td className="px-4 py-3 text-sm text-slate-600">{entry.month}</td>
            <td className="px-4 py-3">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                {entry.recruiter}
              </span>
            </td>
            <td className="px-4 py-3" onClick={(e) => handleStatusClick(entry, e)}>
              {entry.status ? (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${getStatusColor(entry.status)}`}>
                  {entry.status}
                </span>
              ) : (
                <span className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">Click to set</span>
              )}
            </td>
            <td className="px-4 py-3">
              {entry.svoj_trosak ? (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSvojTrosakColor(entry.svoj_trosak)}`}>
                  {entry.svoj_trosak}
                </span>
              ) : '-'}
            </td>
            <td className="px-4 py-3 text-sm text-slate-600">{entry.yard_recovery || '-'}</td>
            <td className="px-4 py-3 text-sm text-slate-600">{entry.cdl_scan || '-'}</td>
            <td className="px-4 py-3 text-sm text-slate-600">{entry.length || '-'}</td>
          </>
        )}
      </tr>
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Calendar className="w-8 h-8 text-purple-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Dolasci/Odlasci 2026</h1>
                <p className="text-slate-600">Track driver arrivals and departures</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Entry
            </button>
          </div>

          {/* Filters */}
          <div className="mt-6 flex flex-wrap gap-4">
            <div className="flex-1 min-w-[300px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by name, email, or recruiter..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Months</option>
              {months.map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
            <select
              value={filterRecruiter}
              onChange={(e) => setFilterRecruiter(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Recruiters</option>
              {recruiters.map(recruiter => (
                <option key={recruiter} value={recruiter}>{recruiter}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Add Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
                <h2 className="text-xl font-bold text-slate-900">Add New Entry</h2>
                <button onClick={() => setShowAddForm(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={newEntry.name}
                    onChange={(e) => setNewEntry({ ...newEntry, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <select
                    value={newEntry.email || ''}
                    onChange={(e) => setNewEntry({ ...newEntry, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">None</option>
                    <option value="RECRUTIING zvao">RECRUTIING zvao</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Month *</label>
                  <select
                    value={newEntry.month}
                    onChange={(e) => setNewEntry({ ...newEntry, month: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select Month</option>
                    {months.map(month => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Recruiter *</label>
                  <select
                    value={newEntry.recruiter}
                    onChange={(e) => setNewEntry({ ...newEntry, recruiter: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select Recruiter</option>
                    {recruiters.map(recruiter => (
                      <option key={recruiter} value={recruiter}>{recruiter}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type *</label>
                  <select
                    value={newEntry.type}
                    onChange={(e) => setNewEntry({ ...newEntry, type: e.target.value as 'dolasci' | 'odlasci' })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="dolasci">Dolasci (Arrival)</option>
                    <option value="odlasci">Odlasci (Departure)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                  <select
                    value={newEntry.status || ''}
                    onChange={(e) => setNewEntry({ ...newEntry, status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select Status</option>
                    {statusOptions.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Svoj Trosak</label>
                  <select
                    value={newEntry.svoj_trosak || ''}
                    onChange={(e) => setNewEntry({ ...newEntry, svoj_trosak: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select Option</option>
                    {svojTrosakOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Yard/Recovery</label>
                  <select
                    value={newEntry.yard_recovery || ''}
                    onChange={(e) => setNewEntry({ ...newEntry, yard_recovery: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">None</option>
                    <option value="Yard">Yard</option>
                    <option value="Recovery">Recovery</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">CDL Scan</label>
                  <select
                    value={newEntry.cdl_scan || ''}
                    onChange={(e) => setNewEntry({ ...newEntry, cdl_scan: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">None</option>
                    <option value="Not Needed">Not Needed</option>
                    <option value="Posted">Posted</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Length</label>
                  <input
                    type="text"
                    value={newEntry.length || ''}
                    onChange={(e) => setNewEntry({ ...newEntry, length: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Subitems</label>
                  <textarea
                    value={newEntry.subitems || ''}
                    onChange={(e) => setNewEntry({ ...newEntry, subitems: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdd}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Add Entry
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Status Change Modal */}
        {showStatusModal && selectedEntryForStatus && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">Change Status</h2>
                <button onClick={() => setShowStatusModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <p className="text-sm text-slate-600 mb-2">Driver: <strong>{selectedEntryForStatus.name}</strong></p>
                  <p className="text-sm text-slate-600">Current Status: <strong>{selectedEntryForStatus.status || 'None'}</strong></p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">New Status</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select Status</option>
                    {statusOptions.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusUpdate}
                  disabled={!newStatus}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Categories Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <table className="w-full">
            <tbody>
              {/* DOLASCI Section */}
              <tr>
                <td colSpan={10} className="p-0">
                  <div
                    onClick={() => setDolasciExpanded(!dolasciExpanded)}
                    className="px-4 py-3 cursor-pointer flex items-center justify-between bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border-l-4 border-blue-500"
                  >
                    <div>
                      <h3 className="font-bold text-slate-900 text-base">DOLASCI (Arrivals)</h3>
                      <p className="text-xs text-slate-600">{dolasciEntries.length} drivers</p>
                    </div>
                    <div className={`transform transition-transform duration-200 ${dolasciExpanded ? 'rotate-180' : ''}`}>
                      <ChevronDown className="w-5 h-5 text-slate-600" />
                    </div>
                  </div>
                </td>
              </tr>
              {dolasciExpanded && (
                <>
                  <tr>
                    <td colSpan={10} className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-12"></th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Month</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Recruiter</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Svoj Trosak</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Yard/Recovery</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">CDL Scan</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Length</th>
                  </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {renderTableRows(dolasciEntries)}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                </>
              )}

              {/* ODLASCI Section */}
              <tr>
                <td colSpan={10} className="p-0">
                  <div
                    onClick={() => setOdlasciExpanded(!odlasciExpanded)}
                    className="px-4 py-3 cursor-pointer flex items-center justify-between bg-gradient-to-r from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 border-l-4 border-red-500"
                  >
                    <div>
                      <h3 className="font-bold text-slate-900 text-base">ODLASCI (Departures)</h3>
                      <p className="text-xs text-slate-600">{odlasciEntries.length} drivers</p>
                    </div>
                    <div className={`transform transition-transform duration-200 ${odlasciExpanded ? 'rotate-180' : ''}`}>
                      <ChevronDown className="w-5 h-5 text-slate-600" />
                    </div>
                  </div>
                </td>
              </tr>
              {odlasciExpanded && (
                <>
                  <tr>
                    <td colSpan={10} className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-12"></th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Month</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Recruiter</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Svoj Trosak</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Yard/Recovery</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">CDL Scan</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Length</th>
                  </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {renderTableRows(odlasciEntries)}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Stats Footer */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between text-sm text-slate-600">
            <div className="flex gap-6">
              <span>Dolasci: <strong className="text-blue-600">{dolasciEntries.length}</strong></span>
              <span>Odlasci: <strong className="text-red-600">{odlasciEntries.length}</strong></span>
              <span>Total: <strong className="text-slate-900">{entries.length}</strong></span>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        {selectedIds.length > 0 && (
          <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-bottom">
            <div className="bg-white rounded-xl shadow-2xl border border-slate-200 px-6 py-4 flex items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                  {selectedIds.length}
                </div>
                <span className="font-semibold text-slate-900">
                  {selectedIds.length === 1 ? 'Driver selected' : `${selectedIds.length} Drivers selected`}
                </span>
              </div>
              <div className="h-8 w-px bg-slate-300"></div>
              <button
                onClick={handleEditSelected}
                className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Edit2 className="w-5 h-5" />
                <span className="font-medium">Edit</span>
              </button>
              <button
                onClick={handleDeleteSelected}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-5 h-5" />
                <span className="font-medium">Delete</span>
              </button>
              <button
                onClick={() => setSelectedIds([])}
                className="ml-2 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
